import type { DetectedIssueAttributes, UserAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'
import type { DetectedIssueType } from '@/client/services/moderation'

import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOrderToSQL,
} from './common'
import DetectedIssueDisputeQ from './DetectedIssueDisputeQ'
import PlatformFeedbackQ from './PlatformFeedbackQ'
import UserQ from './UserQ'

const DetectedIssueQ = {
  ...makeQueryModelV2<DetectedIssueAttributes>('detected_issue')([
    'id',
    'type',
    'entity_type',
    'user_id',
    'reporter_user_id',
    'strike_voided_at',
    'strike_voided_reason',
    'strike_voided_by',
    'safety_report_id',
    'content',
    'key',
    'payload',
    'occurred_at',
    'content_flag_id',
    'created_at',
    'updated_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    User: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: DetectedIssueQ.user_id,
    },
    SafetyReport: {
      query: PlatformFeedbackQ,
      foreignKey: PlatformFeedbackQ.id,
      sourceKey: DetectedIssueQ.safety_report_id,
    },
    Dispute: {
      query: DetectedIssueDisputeQ,
      foreignKey: DetectedIssueDisputeQ.detected_issue_id,
      sourceKey: DetectedIssueQ.id,
    },
  }),

  getUser: (): SQLFragment<UserAttributes> =>
    SQL`${UserQ.where({
      id: DetectedIssueQ.user_id,
    })}`,

  getReporterUser: (): SQLFragment<UserAttributes> =>
    UserQ.where({
      id: DetectedIssueQ.reporter_user_id,
    }),

  getSafetyReportsOfType: (): SQLFragment<UserAttributes> =>
    PlatformFeedbackQ.where(
      {
        detectedIssueType: DetectedIssueQ.type,
      },
      PlatformFeedbackQ.hasSubjectUser(DetectedIssueQ.user_id)
    ),

  hasPayloadValue: (
    key: string,
    value: number | string | SQLFragment<unknown>
  ): SQLFragment<UserAttributes> =>
    SQL`${DetectedIssueQ.payload}->>${key} = CAST(${value} AS TEXT)`,

  isForSession: (sessionId: number | SQLFragment<number>): SQLFragment<boolean> =>
    SQL`(
      ${DetectedIssueQ.payload}->>'entity' IN (${['session-host', 'session-registration', 'session-comment']})
      AND ${DetectedIssueQ.hasPayloadValue('sessionId', sessionId)}
    )`,

  where: (
    {
      id,
      userId,
      reporterUserId,
      safetyReportId,
      contentFlagId,
      key,
      type,
      include = [],
      groupBy = null,
      order = null,
      limit = null,
    }: {
      id?: number | null | SQLFragment<number>
      userId?: number | null | SQLFragment<number>
      reporterUserId?: number | null | SQLFragment<number>
      safetyReportId?: number | null | SQLFragment<number>
      contentFlagId?: number | null | SQLFragment<number>
      key?: string | null
      type?: DetectedIssueType | null | SQLFragment<DetectedIssueType>
      include?: IncludeAssociations
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      limit?: number | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<DetectedIssueAttributes> => SQL`
            FROM ${DetectedIssueQ}
            ${includeAssociationsToSql(DetectedIssueQ, include)}
            WHERE
                ${id != null ? SQL`${DetectedIssueQ.id} = ${id} AND` : SQL``}
                ${userId != null ? SQL`${DetectedIssueQ.user_id} = ${userId} AND` : SQL``}
                ${reporterUserId != null ? SQL`${DetectedIssueQ.reporter_user_id} = ${reporterUserId} AND` : SQL``}
                ${
                  safetyReportId != null
                    ? SQL`${DetectedIssueQ.safety_report_id} = ${safetyReportId} AND`
                    : SQL``
                }
                ${
                  contentFlagId != null
                    ? SQL`${DetectedIssueQ.content_flag_id} = ${contentFlagId} AND`
                    : SQL``
                }
                ${key != null ? SQL`${DetectedIssueQ.key} = ${key} AND` : SQL``}
                ${type != null ? SQL`${DetectedIssueQ.type} = ${type} AND` : SQL``}
                ${customWhere != null ? customWhere : SQL`TRUE`}
            ${queryGroupByToSQL(groupBy)}
            ${queryOrderToSQL(order)}
            ${queryLimitToSQL(limit)}
        `,
}

export default DetectedIssueQ
